import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardActionArea,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  MenuItem,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CartItem } from '../../types';
import {
  restaurantTableApi,
  RestaurantTableDto,
  restaurantActiveSaleApi,
  restaurantActiveSaleItemApi,
  restaurantTableGroupApi,
  RestaurantTableGroupDto,
  restaurantSplitApi,
} from '../../../../../services/restaurantApiService';
import salesService from '../../../../../services/salesService';

type TransferStep = 'selectItems' | 'selectTable' | 'confirmOpen' | 'confirmOccupied';

interface TransferItemModalProps {
  open: boolean;
  onClose: () => void;
  cart: CartItem[];
  currentTableId: number;
  currentTableName: string;
  restaurantScreenId: number;
  restaurantBranchId?: string | null;
  waiterName?: string | null;
  companyId?: string | null;
  onTransferComplete: () => void;
  addWaiterAction: (activeSaleId: string | number, actionType: string, details: any) => Promise<void>;
}

const TransferItemModal: React.FC<TransferItemModalProps> = ({
  open,
  onClose,
  cart,
  currentTableId,
  currentTableName,
  restaurantScreenId,
  restaurantBranchId,
  waiterName,
  companyId,
  onTransferComplete,
  addWaiterAction,
}) => {
  const [step, setStep] = useState<TransferStep>('selectItems');
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [tables, setTables] = useState<RestaurantTableDto[]>([]);
  const [tableGroups, setTableGroups] = useState<RestaurantTableGroupDto[]>([]);
  const [activeTables, setActiveTables] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('all');
  const [selectedTable, setSelectedTable] = useState<RestaurantTableDto | null>(null);
  const [groupMenuAnchor, setGroupMenuAnchor] = useState<null | HTMLElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (open) {
      setStep('selectItems');
      setSelectedItemIds(new Set());
      setSelectedTable(null);
      setSelectedGroupId('all');
      setError(null);
    }
  }, [open]);

  const selectedItems = useMemo(() => {
    return cart.filter(item => selectedItemIds.has(item.cartItemId));
  }, [cart, selectedItemIds]);

  const toggleItem = (cartItemId: string) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(cartItemId)) {
        next.delete(cartItemId);
      } else {
        next.add(cartItemId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedItemIds.size === cart.length) {
      setSelectedItemIds(new Set());
    } else {
      setSelectedItemIds(new Set(cart.map(item => item.cartItemId)));
    }
  };

  const loadTables = async () => {
    try {
      setLoading(true);
      setError(null);

      const [allTables, groups, activeSales] = await Promise.all([
        restaurantTableApi.getByRestaurantScreenId(restaurantScreenId),
        restaurantTableGroupApi.getByRestaurantScreenId(restaurantScreenId),
        restaurantActiveSaleApi.getByRestaurantScreenId(restaurantScreenId),
      ]);

      const activeTableIds = new Set(activeSales.map(sale => sale.TableId));
      setActiveTables(activeTableIds);

      const sortedGroups = groups.sort((a, b) =>
        a.Name.localeCompare(b.Name, undefined, { numeric: true, sensitivity: 'base' })
      );
      setTableGroups(sortedGroups);

      const filteredTables = allTables
        .filter(t => t.Id !== currentTableId)
        .sort((a, b) =>
          a.Name.localeCompare(b.Name, undefined, { numeric: true, sensitivity: 'base' })
        );
      setTables(filteredTables);
    } catch (err: any) {
      setError('Masalar yüklenemedi: ' + (err.response?.data?.Message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToTableSelection = () => {
    if (selectedItemIds.size === 0) return;
    loadTables();
    setStep('selectTable');
  };

  const handleTableSelect = (table: RestaurantTableDto) => {
    setSelectedTable(table);
  };

  const handleTableConfirm = () => {
    if (!selectedTable) return;
    if (activeTables.has(selectedTable.Id)) {
      setStep('confirmOccupied');
    } else {
      setStep('confirmOpen');
    }
  };

  const handleTransfer = async (openNewTable: boolean) => {
    if (!selectedTable || selectedItems.length === 0) return;

    try {
      setIsProcessing(true);
      setError(null);

      const transferredItemsSummary = selectedItems.map(item => ({
        productId: item.id,
        productName: item.name,
        quantity: item.quantity,
        unitPrice: item.price,
        sqlItemId: item.sqlItemId,
      }));

      const sourceActiveSale = await restaurantActiveSaleApi.getByTableId(currentTableId);
      if (!sourceActiveSale) {
        setError('Kaynak masada aktif satış bulunamadı.');
        setIsProcessing(false);
        return;
      }

      let targetActiveSaleId: number;

      if (openNewTable) {
        const turkeyTime = new Date().toISOString();
        let reservedLongCode: string | undefined;
        let reservedShortCode: string | undefined;

        try {
          const orderNumResp = await salesService.reserveOrderNumber('RESTAURANT', restaurantBranchId || undefined);
          reservedLongCode = orderNumResp.longCode;
          reservedShortCode = orderNumResp.shortCode;
        } catch {
          // fallback handled by salesService
        }

        const newSale = await restaurantActiveSaleApi.create({
          RestaurantScreenId: restaurantScreenId,
          BranchId: restaurantBranchId,
          TableId: selectedTable.Id,
          OpenedBy: waiterName || 'Bilinmeyen',
          Notes: `${selectedTable.Name} - Ürün aktarma ile açıldı`,
          OpenedAt: turkeyTime,
          ReservedLongCode: reservedLongCode,
          ReservedShortCode: reservedShortCode,
        });
        targetActiveSaleId = newSale.Id;
      } else {
        const existingSale = await restaurantActiveSaleApi.getByTableId(selectedTable.Id);
        if (!existingSale) {
          setError('Hedef masada aktif satış bulunamadı.');
          setIsProcessing(false);
          return;
        }
        targetActiveSaleId = existingSale.Id;
      }

      // ✅ Ürünü backend move-item ile taşı: PrintedQuantity (mutfağa yazdırıldı
      // bilgisi) sunucu tarafında korunur, kaynak/hedef toplamları yeniden
      // hesaplanır. create+delete yerine bu kullanılır (yoksa taşınan ürün
      // hedef masada "yazdırılmamış/yeni" gibi görünüyordu).
      for (const item of selectedItems) {
        if (!item.sqlItemId) {
          console.warn('[ÜRÜN AKTAR] sqlItemId yok, ürün atlandı:', item.name);
          continue;
        }
        await restaurantSplitApi.moveItem({
          ItemId: item.sqlItemId,
          FromSaleId: sourceActiveSale.Id,
          ToSaleId: targetActiveSaleId,
          Quantity: item.quantity,
          MovedBy: waiterName || undefined,
        });
      }

      // Hedef masanın WaiterActionsJson'una kaydet
      await addWaiterAction(targetActiveSaleId, 'receive_transferred_items', {
        fromTableId: currentTableId,
        fromTableName: currentTableName,
        toTableId: selectedTable.Id,
        toTableName: selectedTable.Name,
        items: transferredItemsSummary,
        itemCount: transferredItemsSummary.length,
        tableOpenedForTransfer: openNewTable,
      });

      // Kaynak masada kalan ürünleri kontrol et
      const remainingItems = await restaurantActiveSaleItemApi.getByActiveSaleId(sourceActiveSale.Id);

      if (remainingItems.length === 0) {
        // Kaynak masada ürün kalmadı - WaiterActionsJson'a yaz ve completeSale ile kapat
        await addWaiterAction(sourceActiveSale.Id, 'transfer_items_close', {
          fromTableId: currentTableId,
          fromTableName: currentTableName,
          toTableId: selectedTable.Id,
          toTableName: selectedTable.Name,
          items: transferredItemsSummary,
          itemCount: transferredItemsSummary.length,
          reason: 'Tüm ürünler başka masaya aktarıldığı için adisyon kapatıldı',
        });

        await restaurantActiveSaleApi.completeSale(sourceActiveSale.Id, {
          subTotal: 0,
          discountAmount: 0,
          taxAmount: 0,
          totalAmount: 0,
          paymentMethod: 'Ürün Aktarma',
          completedBy: waiterName || 'Bilinmeyen',
          completedByName: waiterName || 'Bilinmeyen',
          closedByWaiterName: waiterName || 'Bilinmeyen',
          notes: `Tüm ürünler ${selectedTable.Name} masasına aktarıldı. Adisyon otomatik kapatıldı.`,
        });
      } else {
        // Kaynak masada ürün kaldı - sadece WaiterActionsJson'a yaz
        await addWaiterAction(sourceActiveSale.Id, 'transfer_items', {
          fromTableId: currentTableId,
          fromTableName: currentTableName,
          toTableId: selectedTable.Id,
          toTableName: selectedTable.Name,
          items: transferredItemsSummary,
          itemCount: transferredItemsSummary.length,
          remainingItemCount: remainingItems.length,
        });
      }

      onTransferComplete();
    } catch (err: any) {
      console.error('[ÜRÜN AKTAR] Hata:', err);
      setError('Ürün aktarma başarısız: ' + (err.response?.data?.Message || err.message));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setStep('selectItems');
      setSelectedItemIds(new Set());
      setSelectedTable(null);
      setSelectedGroupId('all');
      setError(null);
      onClose();
    }
  };

  const filteredTables = tables.filter(table => {
    if (selectedGroupId === 'all') return true;
    if (selectedGroupId === 'ungrouped') return !table.TableGroupId;
    return table.TableGroupId?.toString() === selectedGroupId;
  });

  const getSelectedGroupName = () => {
    if (selectedGroupId === 'all') return 'Tüm Masalar';
    if (selectedGroupId === 'ungrouped') return 'Diğer Masalar';
    const group = tableGroups.find(g => g.Id.toString() === selectedGroupId);
    return group?.Name || 'Grup Seç';
  };

  const renderItemSelection = () => (
    <>
      <Alert severity="info" sx={{ m: { xs: 1, md: 2 }, mb: 1 }}>
        <Typography variant="body2">
          <strong>{currentTableName}</strong> masasından aktarmak istediğiniz ürünleri seçin.
        </Typography>
      </Alert>

      <Box sx={{ px: { xs: 1, md: 2 }, pb: 1 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', py: 0.5, '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, px: 1 }}
          onClick={toggleAll}
        >
          <Checkbox
            checked={selectedItemIds.size === cart.length && cart.length > 0}
            indeterminate={selectedItemIds.size > 0 && selectedItemIds.size < cart.length}
            size="small"
          />
          <Typography variant="body2" fontWeight="bold">
            Tümünü Seç ({cart.length} ürün)
          </Typography>
        </Box>
        <Divider />
      </Box>

      <List sx={{ px: { xs: 0.5, md: 1 }, pt: 0, maxHeight: { xs: 350, md: 400 }, overflowY: 'auto' }}>
        {cart.map((item) => {
          const isSelected = selectedItemIds.has(item.cartItemId);
          const itemTotal = item.price * item.quantity;
          return (
            <ListItem
              key={item.cartItemId}
              dense
              disablePadding
              onClick={() => toggleItem(item.cartItemId)}
              sx={{
                cursor: 'pointer',
                py: 0.5,
                px: 1,
                '&:hover': { bgcolor: 'action.hover' },
                bgcolor: isSelected ? 'primary.50' : 'transparent',
                borderRadius: 1,
                mb: 0.5,
                border: isSelected ? '1px solid' : '1px solid transparent',
                borderColor: isSelected ? 'primary.main' : 'transparent',
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox checked={isSelected} size="small" tabIndex={-1} />
              </ListItemIcon>
              <ListItemText
                primary={item.name}
                secondary={`${item.quantity} adet × ${item.price.toFixed(2)} ₺`}
                primaryTypographyProps={{ variant: 'body2', fontWeight: isSelected ? 'bold' : 'normal' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              <Typography variant="body2" fontWeight="bold" color="primary.main">
                {itemTotal.toFixed(2)} ₺
              </Typography>
            </ListItem>
          );
        })}
      </List>
    </>
  );

  const renderTableSelection = () => (
    <>
      <Alert severity="info" sx={{ m: { xs: 1, md: 2 }, mb: 1 }}>
        <Typography variant="body2">
          <strong>{selectedItems.length} ürün</strong> seçildi. Hangi masaya aktarmak istiyorsunuz?
        </Typography>
      </Alert>

      {isMobile && (
        <Box sx={{ px: 1, pb: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={(e) => setGroupMenuAnchor(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{ justifyContent: 'space-between', textTransform: 'none' }}
          >
            {getSelectedGroupName()}
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', height: { xs: 'auto', md: 'calc(100% - 80px)' } }}>
        {!isMobile && (
          <Box sx={{ width: 200, minWidth: 200, bgcolor: '#f5f7fa', borderRight: '1px solid #e0e0e0', p: 1.5, overflowY: 'auto' }}>
            <ToggleButtonGroup
              orientation="vertical"
              value={selectedGroupId}
              exclusive
              onChange={(_, v) => v && setSelectedGroupId(v)}
              sx={{
                width: '100%',
                flexDirection: 'column',
                gap: 0.5,
                '& .MuiToggleButton-root': {
                  fontSize: '0.85rem',
                  border: '1px solid #d0d7de !important',
                  borderRadius: '6px !important',
                  py: 0.75,
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                },
              }}
            >
              <ToggleButton
                value="all"
                sx={{
                  bgcolor: selectedGroupId === 'all' ? 'primary.main' : 'white',
                  color: selectedGroupId === 'all' ? 'white' : 'text.primary',
                  '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
                }}
              >
                <AllInclusiveIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                Tümü
              </ToggleButton>
              {tableGroups.map((group) => (
                <ToggleButton
                  key={group.Id}
                  value={group.Id.toString()}
                  sx={{
                    bgcolor: selectedGroupId === group.Id.toString() ? 'primary.main' : 'white',
                    color: selectedGroupId === group.Id.toString() ? 'white' : 'text.primary',
                    '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
                  }}
                >
                  {group.Name}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}

        <Box sx={{ flex: 1, p: { xs: 1, md: 2 }, overflowY: 'auto', maxHeight: { xs: 400, md: 'none' } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : filteredTables.length === 0 ? (
            <Alert severity="warning">
              {selectedGroupId === 'all' ? 'Taşınabilecek masa bulunamadı' : 'Bu grupta masa bulunamadı'}
            </Alert>
          ) : (
            <Grid container spacing={{ xs: 0.75, md: 1 }}>
              {filteredTables.map((table) => {
                const isActive = activeTables.has(table.Id);
                const isSelected = selectedTable?.Id === table.Id;
                return (
                  <Grid item xs={4} sm={3} md={2.4} lg={2} xl={1.5} key={table.Id}>
                    <Card
                      sx={{
                        bgcolor: isSelected ? 'primary.main' : (isActive ? 'success.main' : 'background.paper'),
                        border: '2px solid',
                        borderColor: isSelected ? 'primary.dark' : (isActive ? 'success.dark' : 'divider'),
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: isSelected ? 'primary.dark' : 'primary.light', boxShadow: 3 },
                      }}
                      elevation={isSelected ? 4 : (isActive ? 2 : 1)}
                    >
                      <CardActionArea onClick={() => handleTableSelect(table)} disabled={isProcessing} sx={{ p: { xs: 0.75, md: 1 } }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: { xs: 0.2, md: 0.3 } }}>
                          <TableRestaurantIcon sx={{ fontSize: { xs: 24, sm: 26, md: 28 }, color: (isSelected || isActive) ? 'white' : 'primary.main' }} />
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{
                              fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                              color: (isSelected || isActive) ? 'white' : 'text.primary',
                              textAlign: 'center',
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                            }}
                          >
                            {table.Name}
                          </Typography>
                          {isActive && (
                            <Chip
                              label="Dolu"
                              size="small"
                              sx={{
                                height: { xs: 14, md: 16 },
                                fontSize: { xs: '0.55rem', md: '0.6rem' },
                                bgcolor: 'rgba(255,255,255,0.3)',
                                color: 'white',
                              }}
                            />
                          )}
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Box>
    </>
  );

  const renderConfirmOpen = () => (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <TableRestaurantIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        Masa Kapalı
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        <strong>{selectedTable?.Name}</strong> masası şu an kapalı.
        Masayı açmak ve seçilen <strong>{selectedItems.length} ürünü</strong> bu masaya aktarmak istiyor musunuz?
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    </Box>
  );

  const renderConfirmOccupied = () => (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <TableRestaurantIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        Masa Açık
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        <strong>{selectedTable?.Name}</strong> masası açık ve başka ürünler var.
        Yine de seçilen <strong>{selectedItems.length} ürünü</strong> bu masaya aktarmak istediğinize emin misiniz?
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    </Box>
  );

  const renderDialogActions = () => {
    switch (step) {
      case 'selectItems':
        return (
          <>
            <Button onClick={handleClose} variant="outlined" size={isMobile ? 'small' : 'medium'}>
              İptal
            </Button>
            <Button
              onClick={handleProceedToTableSelection}
              disabled={selectedItemIds.size === 0}
              variant="contained"
              color="primary"
              size={isMobile ? 'small' : 'medium'}
              startIcon={<DriveFileMoveIcon />}
            >
              Aktar ({selectedItemIds.size} ürün)
            </Button>
          </>
        );
      case 'selectTable':
        return (
          <>
            <Button onClick={() => setStep('selectItems')} variant="outlined" size={isMobile ? 'small' : 'medium'}>
              Geri
            </Button>
            <Button
              onClick={handleTableConfirm}
              disabled={!selectedTable}
              variant="contained"
              color="primary"
              size={isMobile ? 'small' : 'medium'}
              startIcon={<DriveFileMoveIcon />}
            >
              Bu Masaya Aktar
            </Button>
          </>
        );
      case 'confirmOpen':
        return (
          <>
            <Button onClick={() => setStep('selectTable')} disabled={isProcessing} variant="outlined" size={isMobile ? 'small' : 'medium'}>
              Geri
            </Button>
            <Button
              onClick={() => handleTransfer(true)}
              disabled={isProcessing}
              variant="contained"
              color="warning"
              size={isMobile ? 'small' : 'medium'}
              startIcon={isProcessing ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              {isProcessing ? 'Aktarılıyor...' : 'Masayı Aç ve Taşı'}
            </Button>
          </>
        );
      case 'confirmOccupied':
        return (
          <>
            <Button onClick={() => setStep('selectTable')} disabled={isProcessing} variant="outlined" size={isMobile ? 'small' : 'medium'}>
              Geri
            </Button>
            <Button
              onClick={() => handleTransfer(false)}
              disabled={isProcessing}
              variant="contained"
              color="success"
              size={isMobile ? 'small' : 'medium'}
              startIcon={isProcessing ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              {isProcessing ? 'Aktarılıyor...' : 'Evet, Aktar'}
            </Button>
          </>
        );
    }
  };

  const getTitle = () => {
    switch (step) {
      case 'selectItems': return 'Ürün Seç';
      case 'selectTable': return 'Masa Seç';
      case 'confirmOpen': return 'Masa Aç ve Aktar';
      case 'confirmOccupied': return 'Aktarma Onayı';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={step === 'selectTable' ? 'lg' : 'sm'}
      fullWidth
      fullScreen={isMobile && step === 'selectTable'}
      disableEscapeKeyDown={isProcessing}
    >
      <DialogTitle sx={{ px: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DriveFileMoveIcon color="primary" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
          <Typography variant="h6" component="span" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
            Ürün Aktarma - {getTitle()}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: step === 'selectTable' ? { xs: 'auto', md: 500 } : 'auto' }}>
        {error && step !== 'confirmOpen' && step !== 'confirmOccupied' && (
          <Alert severity="error" sx={{ mx: { xs: 1, md: 2 }, mb: 1 }}>{error}</Alert>
        )}
        {step === 'selectItems' && renderItemSelection()}
        {step === 'selectTable' && renderTableSelection()}
        {step === 'confirmOpen' && renderConfirmOpen()}
        {step === 'confirmOccupied' && renderConfirmOccupied()}
      </DialogContent>

      <Menu
        anchorEl={groupMenuAnchor}
        open={Boolean(groupMenuAnchor)}
        onClose={() => setGroupMenuAnchor(null)}
        PaperProps={{ sx: { maxHeight: 400, width: '250px' } }}
      >
        <MenuItem selected={selectedGroupId === 'all'} onClick={() => { setSelectedGroupId('all'); setGroupMenuAnchor(null); }}>
          <AllInclusiveIcon sx={{ mr: 1, fontSize: '1rem' }} /> Tüm Masalar
        </MenuItem>
        {tableGroups.map((group) => (
          <MenuItem
            key={group.Id}
            selected={selectedGroupId === group.Id.toString()}
            onClick={() => { setSelectedGroupId(group.Id.toString()); setGroupMenuAnchor(null); }}
          >
            {group.Name}
          </MenuItem>
        ))}
      </Menu>

      <DialogActions sx={{ px: { xs: 2, md: 3 }, pb: 2, borderTop: '1px solid #e0e0e0' }}>
        {renderDialogActions()}
      </DialogActions>
    </Dialog>
  );
};

export default TransferItemModal;
